/**
 * L'annuaire des transporteurs, vu par l'écran.
 *
 * **Exactement les champs d'un client.** Le modèle a été arrêté le 03/09/2026
 * après lecture de la base réelle de TNS, qui portait en plus une colonne
 * `source` (« Cargo » pour les reprises de l'ancien logiciel, « Carnet » pour
 * les saisies à la main). **Elle a été retirée de l'écran et de l'API le
 * 03/09/2026, à la demande de l'utilisateur** : aucune des deux valeurs ne
 * décrit une fiche créée dans Stafa. La colonne reste en base — voir
 * `apps/api/src/transporteurs.ts`.
 *
 * Ce qui distingue encore un transporteur d'un client tient donc en deux
 * phrases, et c'est voulu : **le sens des conditions s'inverse.**
 * `conditions_paiement` et `encours_autorise` disent ici ce que NOUS devons au
 * transporteur, là où pour un client ils disent ce qu'il nous doit. Même
 * colonne, sens contraire — mieux vaut que l'écran le dise que de laisser
 * deviner.
 *
 * L'ordre des champs est celui du client, et les `court` y vont **par
 * paires** : code et SIRET, TVA et pays, code postal et ville, téléphone et
 * courriel, conditions et encours. Un `court` isolé laisserait un trou à sa
 * droite — constaté à l'écran le 03/09/2026.
 */

import type { Annuaire } from './annuaire.js';

export const ANNUAIRE_TRANSPORTEURS: Annuaire = {
  chemin: 'transporteurs',
  cleListe: 'transporteurs',
  cleFiche: 'transporteur',
  singulier: 'transporteur',
  pluriel: 'transporteurs',
  // Le seul annuaire qui porte du matériel et des zones, à ce jour.
  couverture: true,
  conditions: true,
  noteConditions:
    'Ces conditions sont celles que NOUS lui devons — l’inverse d’une fiche client. L’encours est enregistré, mais aucune règle ne s’appuie encore dessus.',
  noteDesactive:
    'Il reste consultable, ainsi que les transports qui lui ont déjà été confiés.',
  champs: [
    { cle: 'nom', etiquette: 'Nom', max: 200, obligatoire: true },
    { cle: 'code', etiquette: 'Code', max: 32, chiffre: true, court: true },
    { cle: 'siret', etiquette: 'SIRET', max: 14, chiffre: true, court: true },
    { cle: 'tva', etiquette: 'N° de TVA', max: 32, chiffre: true, court: true },
    { cle: 'pays', etiquette: 'Pays', max: 80, court: true, liste: 'pays' },
    { cle: 'adresse', etiquette: 'Adresse', max: 500 },
    {
      cle: 'code_postal',
      etiquette: 'Code postal',
      max: 16,
      chiffre: true,
      court: true,
    },
    { cle: 'ville', etiquette: 'Ville', max: 120, court: true },
    { cle: 'telephone', etiquette: 'Téléphone', max: 40, chiffre: true, court: true },
    { cle: 'courriel', etiquette: 'Courriel', max: 200, court: true },
    { cle: 'site_web', etiquette: 'Site', max: 200 },
    {
      cle: 'conditions_paiement',
      etiquette: 'Conditions de paiement',
      max: 200,
      court: true,
      liste: 'conditions',
    },
    {
      cle: 'encours_autorise',
      etiquette: 'Encours autorisé',
      max: 20,
      chiffre: true,
      court: true,
    },
    { cle: 'note', etiquette: 'Note', max: 2000, long: true },
  ],
};
